/**
 * 클라이언트 <-> 서버 와이어 프로토콜.
 * 서버(Durable Object)와 클라이언트가 동일한 정의를 공유한다.
 */

export const PROTOCOL_VERSION = 1;

/** 클라이언트가 자기 위치를 보고하는 주기. 서버는 받는 즉시 중계하므로 이게 곧 틱이다. */
export const REPORT_HZ = 15;
/** 원격 플레이어 보간 지연(ms). 스냅샷 간격의 약 2배. */
export const INTERP_DELAY_MS = 140;
/** 허용 최대 수평 이동 속도(m/s). 서버는 이 값으로 보고당 이동 상한을 정한다. */
export const MAX_SPEED = 9;

/**
 * 한 판의 단계.
 *   investigate 조사 (라운드 1~3)
 *   brief       진술 — 한 사람씩. 발표자만 질문하고, 지목받은 사람만 답한다
 *   free        난상 추리 — 전원 발언
 *   vote        지목
 *   result      진범 공개
 */
export type Phase =
  /** 대기실. 사람이 모이는 중이고 아직 배역이 없다 */
  | 'lobby'
  /** 캐릭터를 고르는 중. 먼저 고른 사람이 가져간다 */
  | 'pick'
  /** 배역을 받고 대본을 읽는 중. 다 읽은 사람은 준비를 누른다 */
  | 'read'
  | 'investigate'
  | 'brief'
  | 'free'
  | 'vote'
  | 'result';

/** 칠판에 걸린 단서 카드 */
export interface BoardCard {
  id: string;
  clueId: string;
  /** 건 사람 */
  by: string;
  /** 건 사람이 쓴 10자 이내 요약. **원문이 아니라 주장이다** */
  memo: string;
  at: number;
  /**
   * 걸린 순간 **전원에게 열리는** 원문. 칠판에 건다는 것이 곧 공개다.
   *
   * `clues` 는 본 사람에게만 가지만, 칠판 카드는 모두가 봐야 토론이 된다 —
   * 「말로만 주장」과 「사진을 들이댄다」가 갈리는 것이 이 게임의 핵심이다.
   */
  title: string;
  text: string;
  img?: string;
  /** 건 라운드 — 핀 상한은 이 라운드의 카드만 센다 (사흘 v8, pinLimit 은 회차별) */
  round?: number;
  /** 감정서 자동 게시 — 탐정 명의지만 탐정의 핀 상한을 먹지 않는다 */
  auto?: boolean;
}

/** 상호작용 가능한 오브젝트의 동기화 상태. 필드는 전부 선택적 — 종류마다 쓰는 것만 채운다. */
export interface ObjState {
  /** 문/서랍: 열림 */
  open?: boolean;
  /** 문/금고: 잠김 */
  locked?: boolean;
  /** 조명: 켜짐 */
  on?: boolean;
  /** 픽업 아이템: 주워졌는가 */
  taken?: boolean;
  /** 픽업 아이템 소지자 playerId */
  by?: string;
  /** 내려놓은 위치 (원위치가 아닐 때만) */
  p?: [number, number, number];
  /** 금고 등: 해제됨 */
  solved?: boolean;
  /** 조건이 충족된 뒤에야 「놓이는」 내용물이 들어왔는가 — 진술 메모 같은 배달물 (ForensicDef.deliverTo) */
  stocked?: boolean;
}

/**
 * 발견된 단서 한 건.
 * 제목/본문은 서버만 알고 있다가 발견 시점에 실어 보낸다 (클라 번들에 정답이 들어가지 않도록).
 *
 * **그리고 발견 즉시 전원에게 가지 않는다.** 1차는 같은 조 둘, 2·3차는 발견자 혼자만
 * 본다. 남들은 칠판에 걸려야 본다 — 무엇을 내놓고 무엇을 삼킬지 고르는 것이
 * 이 게임이고, 다 보이면 그 선택이 통째로 사라진다.
 */
export interface ClueRecord {
  id: string;
  title: string;
  text: string;
  /** `/clues/<id>.png` 가 있으면 그 파일명. 없으면 undefined — 카드가 글자만으로 선다 */
  img?: string;
  /** 발견자 playerId */
  by: string;
  /** 발견자 이름 (퇴장 후에도 남도록 스냅샷) */
  byName: string;
  at: number;
  /**
   * **감정 대상.** 이 카드가 칠판에 오르면 오는 감정서의 단서 id (예: 시신 → 부검).
   * 수첩의 폴라로이드에 붉은 돋보기 도장이 찍히는 근거다. 감정서가 오면 봉투로 바뀐다.
   */
  forensic?: string;
}

/** 방 전체의 권위 상태 */
export interface WorldState {
  /**
   * 이 방이 도는 사건. 방을 만들 때 정해지고 **끝까지 안 바뀐다** —
   * 도중에 갈면 저장된 물건 id 가 통째로 어긋나 판이 깨진다.
   *
   * 옛 저장분에는 없어서 optional 이다. 없으면 사흘로 본다.
   */
  episode?: string;
  phase: Phase;
  /**
   * 조사 라운드 (1..3). 라운드마다 열리는 구역과 조사 횟수가 다르다.
   * 이게 없으면 혼자 돌아다니며 E 만 눌러 41개를 다 캐고 게임이 5분에 끝난다.
   */
  round: number;
  /** propId -> 상태 */
  objects: Record<string, ObjState>;
  /** clueId -> 발견 기록 */
  clues: Record<string, ClueRecord>;
  /** playerId -> 이번 라운드에 남은 조사 횟수 */
  budget: Record<string, number>;
  /** playerId -> 1차에 배정된 구역 id. 2인 1조로 나뉜다 */
  assigned: Record<string, string>;
  /** 숙지를 마치고 준비를 누른 사람. 전원이 눌러야 호스트가 시작할 수 있다 */
  ready: Record<string, true>;
  /** 잠금이 풀린 물건. 한 번 열리면 전원에게 열린 채로 남는다 */
  unlocked: Record<string, true>;
  /**
   * **누군가 이미 캐 간 단서 id.** 제목도 본문도 없다.
   *
   * `clues` 는 이제 「내가 볼 수 있는 것」만 담는다(→ ClueRecord 주석).
   * 그런데 서랍이 이미 열려 있는 것 자체는 방에 들어간 누구나 본다 —
   * 그래서 「조사했다」는 사실만 이 목록으로 전원에게 간다.
   * 내용은 발표자가 칠판에 걸어야 열린다.
   */
  taken: Record<string, true>;
  /**
   * **내가 촬영해 둔 단서.** 사람마다 다르다 (서버가 본인 것만 실어 준다).
   * 칠판에 걸려면 여기 있어야 한다.
   */
  shots: Record<string, true>;
  /** 도착한 감정서 id. 전원에게 간다 — 「부검이 왔다」는 사실은 공개다 */
  reports: Record<string, true>;
  /**
   * 지금 발표 중인 사람. '' 이면 없음.
   *
   * **말은 아무나 언제든 할 수 있다.** 발표자라는 건 「지금 칠판에 카드를 거는
   * 사람」이라는 뜻일 뿐이다. 크라임씬의 재미는 서로 끼어들고 받아치는 데서
   * 나오는데, 발언권을 서버가 쥐면 그게 통째로 사라진다.
   */
  speaker: string;
  /** 이번 진술에서 이미 발표한 사람 */
  briefDone: string[];
  /** 이 차례가 끝나는 시각(epoch ms) */
  stageEndsAt: number;
  /** 칠판 */
  board: BoardCard[];
  /** 카드에 「이상하다」를 남긴 사람들. 대기자도 할 일이 있어야 한다 */
  flags: Record<string, string[]>;
  /** 지목. voterId -> targetId */
  votes: Record<string, string>;

  /** 지금 나가 있는 사람. 비어 있으면 아무도 안 나가 있다 */
  out: string[];
  /** 이번 라운드에 이미 다녀온 사람 */
  done: string[];
  /** 짝 신청 — playerId -> 같이 가자고 지목한 상대. 서로 지목하면 그 둘이 나간다 */
  pairing: Record<string, string>;
  /** 나가 있는 조의 시간이 끝나는 시각(epoch ms). 0 이면 아무도 안 나가 있다 */
  turnEndsAt: number;
  /**
   * playerId -> 배역 id. **누가 누구인지는 전원이 안다.**
   * 대본 속살(왜 그런지, 무엇을 숨기는지)은 서버가 본인에게만 보낸다.
   */
  roles: Record<string, string>;
}

/** 월드 상태 변경 단위. 서버만 만들고, 클라는 그대로 적용한다. */
export type WorldOp =
  | { op: 'set'; id: string; f: ObjState }
  /** 단서 전문. **볼 자격이 있는 사람에게만 간다** */
  | { op: 'clue'; c: ClueRecord }
  /** 「누가 캐 갔다」는 사실만. 전원에게 간다 */
  | { op: 'taken'; id: string }
  /** 촬영했다. **찍은 본인에게만** 간다 */
  | { op: 'shot'; id: string }
  /** 감정서가 도착했다. 전원에게 간다 */
  | { op: 'report'; id: string }
  | { op: 'phase'; v: Phase }
  | { op: 'round'; v: number; budget: Record<string, number> }
  | { op: 'budget'; id: string; v: number }
  | { op: 'assign'; a: Record<string, string> }
  | { op: 'unlock'; id: string }
  | { op: 'pairing'; v: Record<string, string> }
  | { op: 'turn'; out: string[]; done: string[]; endsAt: number }
  | { op: 'roles'; v: Record<string, string> }
  | { op: 'ready'; id: string; v: boolean }
  | { op: 'stage'; phase: Phase; speaker: string; done: string[]; endsAt: number }
  | { op: 'board'; v: BoardCard[] }
  | { op: 'flags'; id: string; v: string[] }
  | { op: 'votes'; v: Record<string, string> };

export interface PlayerState {
  id: string;
  name: string;
  /** hex 색상 (아바타/이름표) */
  color: string;
  host: boolean;
}

/** 스냅샷에 실리는 플레이어 동작 상태 (배열로 압축) */
export type PlayerPose = [
  id: string,
  x: number,
  y: number,
  z: number,
  /** yaw (rad) */
  yaw: number,
  /** pitch (rad), 고개 방향 */
  pitch: number,
  /** 비트 플래그: 1=이동중, 2=앉음, 4=가리키는 중 */
  flags: number,
];

export type ClientMsg =
  /** id·tok 이 있으면 **이어 받기** — welcome 에서 받은 것. pose [x, y, z, yaw] 는 다시 붙을 때 서 있던 자리 */
  | { t: 'hello'; v: number; name: string; color: string; id?: string; tok?: string; pose?: [number, number, number, number] }
  | { t: 'move'; x: number; y: number; z: number; yaw: number; pitch: number; flags: number }
  | { t: 'act'; id: string; a: string; arg?: string }
  | { t: 'chat'; m: string }
  /** 월드 한 지점을 가리키기 (상태가 아닌 순간 이벤트) */
  | { t: 'mark'; x: number; y: number; z: number }
  | { t: 'phase'; v: Phase }
  | { t: 'round'; v: number }
  /** 새 판 — 월드를 처음 상태로 되돌린다 (호스트만) */
  | { t: 'reset' }
  /** 잠긴 것을 여는 시도. code 는 번호 자물쇠일 때만. 실패해도 조사 횟수는 안 나간다 */
  | { t: 'unlock'; id: string; code?: string }
  /** 같이 나갈 사람 지목. 서로 지목하면 조가 된다. '' 이면 취소 */
  | { t: 'pair'; with: string }
  /**
   * go     — 나가겠다 (혼자 조사 / 전원 조사일 때)
   * return — 돌아왔다 (나가 있는 사람이나 호스트)
   * end    — 이번 라운드 조사를 끝낸다 (호스트)
   */
  | { t: 'turn'; v: 'go' | 'return' | 'end' }
  /** 배역 배분 (호스트만). 섞어서 나눠 준다 */
  | { t: 'roles' }
  /** 대본을 다 읽었다 / 다시 읽겠다 */
  | { t: 'ready'; v: boolean }
  /** 캐릭터를 고른다. 이미 남이 가져갔으면 거부된다 */
  | { t: 'pick'; castId: string }
  /** 내 대본 주세요. 서버가 **나에게만** 보낸다 */
  | { t: 'script' }
  /** 단계 넘기기 (호스트) */
  | { t: 'stage'; v: Phase }
  /** 조사 직후 10자 요약. **안 써 두면 칠판에 못 건다** */
  | { t: 'memo'; clueId: string; text: string }
  /** 탐정이 발표자를 지목한다. '' 이면 발표 종료 */
  | { t: 'speaker'; id: string }
  /** 발표자가 카드를 칠판에 건다 */
  /**
   * 찾은 것을 **촬영한다.**
   *
   * 크라임씬의 폴라로이드다 — 찾았다고 다 쓸 수 있는 게 아니라, 찍어 둔 것만
   * 나중에 칠판에 걸 수 있다. 조사 시간에 「이건 찍고 저건 안 찍는다」를
   * 고르게 만드는 장치이고, 안 찍은 것은 말로만 주장해야 한다.
   */
  | { t: 'shot'; clueId: string }
  /** 시신 부위 조사 — 목·손·주머니… 단서가 없는 부위도 눌러 본다 */
  | { t: 'bodypart'; part: string }
  | { t: 'pin'; clueId: string }
  /** 대기자가 카드에 「이상하다」를 남긴다 (토글) */
  | { t: 'flag'; cardId: string }
  /** 지목 */
  | { t: 'ballot'; id: string }
  | { t: 'ping'; c: number }
  | { t: 'resync' };

export type ServerMsg =
  | {
      t: 'welcome';
      you: string;
      /** 이어 받기 표 — 다시 붙을 때 hello 에 id 와 같이 실어 보낸다 */
      tok?: string;
      room: string;
      now: number;
      rev: number;
      world: WorldState;
      players: PlayerState[];
      poses: PlayerPose[];
    }
  /** 15Hz 플레이어 스냅샷 */
  /** held[i] = 서버가 poses[i] 를 모아 두느라 들고 있던 ms(도쿄 모아 보내기). 받는 쪽은 그만큼 앞당겨 찍어야 샘플 간격이 안 흔들린다 */
  | { t: 'snap'; now: number; poses: PlayerPose[]; held?: number[] }
  /** 월드 델타. rev 는 ops 적용 '후'의 리비전. */
  | { t: 'world'; rev: number; ops: WorldOp[] }
  /** 재동기화 (rev 갭 감지 또는 재접속) */
  | { t: 'full'; rev: number; world: WorldState; players: PlayerState[] }
  | { t: 'join'; p: PlayerState }
  | { t: 'leave'; id: string }
  | { t: 'chat'; id: string; name: string; color: string; m: string; at: number }
  /** 시스템 로그 (단서 발견, 문 개방 등) */
  | { t: 'log'; m: string; at: number }
  | { t: 'mark'; id: string; name: string; color: string; x: number; y: number; z: number }
  /** 행동 거부 사유 (본인에게만) */
  | { t: 'deny'; id: string; why: string }
  /** 조용한 한 줄 (본인에게만) — 열었는데 빈 서랍 「먼지뿐이다」 같은 것. deny 와 달리 실패가 아니다 */
  | { t: 'note'; m: string }
  /** 선택형 조사(choose) — exam 을 마친 기계의 선택지를 열라는 신호 (본인에게만) */
  | { t: 'choose'; id: string }
  /** [R] 음원/영상 재생 (v8 A11) — 누군가 조사 끝난 기계를 틀었다. 방 전원에게 간다 */
  | { t: 'play'; id: string }
  /** 시신 부위 조사 결과. 단서가 있는 부위면 clueId 가 붙고 clue op 도 따로 온다 */
  | { t: 'bodypart'; part: string; text: string; clueId?: string }
  /** 내 대본. 요청한 본인에게만 간다 */
  | {
      t: 'script';
      castId: string;
      culprit: boolean;
      sections: { title: string; body: string }[];
    }
  /** 결과. 전원에게 한 번 간다 */
  | {
      t: 'verdict';
      accused: string;
      culpritCast: string;
      caught: boolean;
      tally: { id: string; n: number }[];
      truth: string;
    }
  | { t: 'pong'; c: number; now: number };

export function encode(msg: ClientMsg | ServerMsg): string {
  return JSON.stringify(msg);
}

export function decode<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
